import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Inbox, Clock, CheckCircle2, XCircle, DollarSign, TrendingUp } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";

const CARDS = [
  { key: "submitted", label: "Submitted", icon: Inbox, color: "#0052FF" },
  { key: "under_review", label: "Under review", icon: Clock, color: "#F59E0B" },
  { key: "approved", label: "Approved", icon: CheckCircle2, color: "#10B981" },
  { key: "rejected", label: "Rejected", icon: XCircle, color: "#EF4444" },
  { key: "paid", label: "Paid", icon: DollarSign, color: "#10B981" },
];

export default function AdminOverviewPage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/overview").then((r) => setData(r.data));
  }, []);

  if (!data) return <PageWrap title="Overview" />;
  const chartData = buildChart(data.recent);

  return (
    <PageWrap title="Overview" data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {CARDS.map((c) => (
          <Stat
            key={c.key}
            label={c.label}
            value={data.counts[c.key] ?? 0}
            Icon={c.icon}
            color={c.color}
            testid={`stat-${c.key}`}
          />
        ))}
        <Stat
          label="Revenue paid"
          value={`$${data.revenue_paid.toLocaleString()}`}
          Icon={TrendingUp}
          color="#0A1628"
          testid="stat-revenue"
        />
      </div>
      <div className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-6 soft-shadow">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-semibold text-[#0A1628]">Daily submissions</h3>
          <span className="text-xs text-[#9CA3AF]">Last 14 days</span>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <XAxis dataKey="d" stroke="#9CA3AF" fontSize={11} />
              <YAxis stroke="#9CA3AF" fontSize={11} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
              <Line type="monotone" dataKey="count" stroke="#0052FF" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden soft-shadow">
        <div className="px-5 py-4 border-b border-[#E5E7EB]">
          <h3 className="font-display text-lg font-semibold text-[#0A1628]">Recent activity</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB] text-xs uppercase tracking-wider text-[#9CA3AF]">
            <tr>
              <th className="text-left px-5 py-3 font-semibold">When</th>
              <th className="text-left px-5 py-3 font-semibold">Status</th>
              <th className="text-right px-5 py-3 font-semibold">Estimated</th>
            </tr>
          </thead>
          <tbody>
            {data.recent.slice(0, 10).map((r, i) => (
              <tr key={i} className="border-t border-[#F3F4F6]">
                <td className="px-5 py-3 text-[#4B5563] font-mono-display text-xs">
                  {new Date(r.created_at).toLocaleString()}
                </td>
                <td className="px-5 py-3 capitalize text-[#0A1628]">{r.status.replace("_", " ")}</td>
                <td className="px-5 py-3 text-right font-mono-display font-semibold text-[#0A1628]">
                  ${(r.estimated_payout || 0).toFixed(2)}
                </td>
              </tr>
            ))}
            {data.recent.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center py-10 text-[#9CA3AF]">No activity yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </PageWrap>
  );
}

function Stat({ label, value, Icon, color, testid }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 soft-shadow" data-testid={testid}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ background: color }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="mt-2 text-xs text-[#9CA3AF] uppercase tracking-wider font-semibold">{label}</div>
      <div className="font-display font-bold text-2xl text-[#0A1628] mt-0.5">{value}</div>
    </div>
  );
}

function PageWrap({ title, children, ...rest }) {
  return (
    <div className="p-6 md:p-8" {...rest}>
      <h1 className="font-display text-2xl font-bold text-[#0A1628] mb-6">{title}</h1>
      {children}
    </div>
  );
}

function buildChart(recent) {
  const map = {};
  for (let i = 13; i >= 0; i--) {
    const d = new Date(Date.now() - i * 86400000);
    const key = d.toISOString().slice(0, 10);
    map[key] = { d: key.slice(5), count: 0 };
  }
  for (const r of recent) {
    const key = (r.created_at || "").slice(0, 10);
    if (map[key]) map[key].count += 1;
  }
  return Object.values(map);
}
